import React, { useState, useEffect } from 'react';
import { Building2, DollarSign, TrendingUp, TrendingDown, Package, RefreshCw, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';

const fmt  = (n, d = 2) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN') : '—';

const Card = ({ icon: Icon, iconColor, label, value, sub, border }) => (
  <div className={`bg-white rounded-xl shadow-sm border p-5 ${border || 'border-gray-100'}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon size={15} className={iconColor} />
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
    </div>
    <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);

const AccountingSummary = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showVendors, setShowVendors] = useState(true);
  const [showSales, setShowSales] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/accounting/summary', { credentials: 'include' });
      const json = await res.json();
      if (json.success) setData(json);
      else setError(json.error || 'Failed to load');
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen text-gray-400">
      <RefreshCw size={24} className="animate-spin mr-2" /> Loading accounting summary…
    </div>
  );

  if (error) return (
    <div className="p-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700">
        <AlertCircle size={16} /> {error}
      </div>
    </div>
  );

  const { vendor_balances, totals_owed, sales_pl, inventory_on_hand, dollar_rate } = data;
  const isProfit = sales_pl.total_profit_usd >= 0;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <PageIdentifier screenId={SCREEN_IDS.VENDORS.ACCOUNTING_SUMMARY} />

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <DollarSign className="text-green-600" size={28} />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Accounting Summary</h1>
              <p className="text-sm text-gray-500">Vendor balances locked at entry-date rates · ₹{fmt(dollar_rate)} / $1 (latest, used for P&L)</p>
            </div>
          </div>
          <button onClick={load}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* ── Top Summary Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card icon={DollarSign} iconColor="text-red-500" label="Total Outstanding (USD)"
            value={`$${fmt(totals_owed.outstanding_usd)}`}
            sub={`Of $${fmt(totals_owed.total_usd)} owed to vendors`}
            border="border-red-100" />

          <Card
            icon={isProfit ? TrendingUp : TrendingDown}
            iconColor={isProfit ? 'text-green-500' : 'text-red-500'}
            label="Net Profit / Loss"
            value={`${isProfit ? '+' : ''}$${fmt(sales_pl.total_profit_usd)}`}
            sub={`From ${sales_pl.items_sold} sold items`}
            border={isProfit ? 'border-green-100' : 'border-red-100'} />

          <Card icon={Package} iconColor="text-blue-500" label="Inventory on Hand"
            value={`$${fmt(inventory_on_hand.total_cost_usd)}`}
            sub={`${inventory_on_hand.item_count} unsold items (₹${fmt(inventory_on_hand.total_cost_inr, 0)} INR)`}
            border="border-blue-100" />
        </div>

        {/* ── Sales P&L Cards ── */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Total Revenue</div>
            <div className="text-xl font-bold text-green-700">${fmt(sales_pl.total_revenue_usd)}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Cost of Goods Sold</div>
            <div className="text-xl font-bold text-gray-700">${fmt(sales_pl.total_cost_usd)}</div>
          </div>
          <div className={`rounded-xl shadow-sm border p-4 text-center ${isProfit ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Gross Profit</div>
            <div className={`text-xl font-bold ${isProfit ? 'text-green-700' : 'text-red-700'}`}>
              {isProfit ? '+' : ''}${fmt(sales_pl.total_profit_usd)}
            </div>
          </div>
        </div>

        {/* ── Vendor Balances Table ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
          <button
            className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition"
            onClick={() => setShowVendors(s => !s)}>
            <div className="flex items-center gap-2">
              {showVendors ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Building2 size={16} className="text-amber-500" />
              <span className="font-semibold text-gray-800">Vendor Balances ({vendor_balances.length})</span>
            </div>
            <div className="flex gap-6 text-sm text-gray-500 hidden sm:flex">
              <span>Outstanding: <strong className="text-red-600">${fmt(totals_owed.outstanding_usd)}</strong></span>
            </div>
          </button>

          {showVendors && (
            <div className="overflow-x-auto border-t border-gray-100">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Vendor</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Items</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">USD Owed</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">USD Paid</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600 text-red-600">USD Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {vendor_balances.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-10 text-gray-400">No vendor data</td></tr>
                  ) : vendor_balances.map(v => (
                    <tr key={v.vendor_id} className="border-t border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{v.vendor_code || v.vendor_name}</div>
                        {v.vendor_company && <div className="text-xs text-gray-400">{v.vendor_company}</div>}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">{v.item_count}</td>
                      <td className="px-4 py-3 text-right text-gray-600">${fmt(v.total_usd_owed)}</td>
                      <td className="px-4 py-3 text-right text-gray-600">${fmt(v.total_paid_usd)}</td>
                      <td className="px-4 py-3 text-right font-bold text-red-600">${fmt(v.outstanding_usd)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr>
                    <td colSpan={2} className="px-4 py-3 font-bold text-gray-700">Totals</td>
                    <td className="px-4 py-3 text-right font-bold text-gray-700">${fmt(totals_owed.total_usd)}</td>
                    <td className="px-4 py-3 text-right font-bold text-gray-700">
                      ${fmt(vendor_balances.reduce((s, v) => s + v.total_paid_usd, 0))}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-red-600">${fmt(totals_owed.outstanding_usd)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* ── Sales P&L Table ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition"
            onClick={() => setShowSales(s => !s)}>
            <div className="flex items-center gap-2">
              {showSales ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <TrendingUp size={16} className="text-green-500" />
              <span className="font-semibold text-gray-800">Sales P&amp;L — {sales_pl.items_sold} items sold</span>
            </div>
            <span className={`text-sm font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
              {isProfit ? '+' : ''}${fmt(sales_pl.total_profit_usd)} net
            </span>
          </button>

          {showSales && (
            <div className="overflow-x-auto border-t border-gray-100">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Item Code</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Sold Date</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Revenue (USD)</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Cost (USD)</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Profit (USD)</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {sales_pl.items.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-10 text-gray-400">No sold items yet</td></tr>
                  ) : sales_pl.items.map((item, i) => (
                    <tr key={i} className="border-t border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono font-semibold text-gray-900">{item.code}</td>
                      <td className="px-4 py-3 text-gray-700">{item.name || '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{fmtDate(item.sold_date)}</td>
                      <td className="px-4 py-3 text-right text-green-700 font-semibold">${fmt(item.revenue_usd)}</td>
                      <td className="px-4 py-3 text-right text-gray-600">${fmt(item.cost_usd)}</td>
                      <td className={`px-4 py-3 text-right font-bold ${item.profit_usd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {item.profit_usd >= 0 ? '+' : ''}${fmt(item.profit_usd)}
                      </td>
                      <td className={`px-4 py-3 text-right text-sm ${item.profit_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {item.profit_pct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
                {sales_pl.items.length > 0 && (
                  <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                    <tr>
                      <td colSpan={3} className="px-4 py-3 font-bold text-gray-700">Totals</td>
                      <td className="px-4 py-3 text-right font-bold text-green-700">${fmt(sales_pl.total_revenue_usd)}</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-700">${fmt(sales_pl.total_cost_usd)}</td>
                      <td className={`px-4 py-3 text-right font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                        {isProfit ? '+' : ''}${fmt(sales_pl.total_profit_usd)}
                      </td>
                      <td className="px-4 py-3" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountingSummary;
